import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Sparkles,
  Image as ImageIcon,
  Trash2,
  Check,
  Smartphone,
  Target,
  UserPlus,
  Info,
  Copy,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { ImageUpload } from './ImageUpload';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ---------- Live WhatsApp-style preview card ----------
// Reusable inside every tab. Reads what the admin is currently typing so the
// preview updates in real time (no need to save first).
const WhatsAppPreviewCard = ({ image, title, description, host }) => (
  <div className="rounded-xl border border-slate-200 bg-[#dcf8c6]/40 p-2.5 shadow-sm">
    <div className="rounded-lg overflow-hidden bg-white border border-slate-200 shadow-sm">
      {image ? (
        <div className="aspect-[1200/628] bg-slate-100 overflow-hidden">
          <img
            src={image}
            alt="Prévia"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
      ) : (
        <div className="aspect-[1200/628] bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center">
          <ImageIcon className="h-8 w-8 text-slate-400" />
        </div>
      )}
      <div className="px-3 py-2">
        <div className="text-[13px] font-semibold text-slate-900 leading-tight line-clamp-2">
          {title}
        </div>
        <div className="text-[11px] text-slate-600 mt-0.5 leading-snug line-clamp-2">
          {description}
        </div>
        <div className="text-[10px] uppercase tracking-wide text-slate-400 mt-1 truncate">
          {host}
        </div>
      </div>
    </div>
  </div>
);

// ---------- Capture-page live preview ----------
const CapturePreviewCard = ({ image, subtitle, leaderName, tenantName, tenantColor }) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden bg-white shadow-sm">
    {image ? (
      <img src={image} alt="Cabeçalho" className="w-full aspect-[1200/628] object-cover" />
    ) : (
      <div
        className="w-full aspect-[1200/628] flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${tenantColor || '#0D1B3D'}22, ${tenantColor || '#0D1B3D'}66)` }}
      >
        <div className="text-center text-slate-500 text-[11px]">
          <ImageIcon className="h-8 w-8 mx-auto opacity-40" />
          <p className="mt-1">Sem banner — usará padrão herdado</p>
        </div>
      </div>
    )}
    <div className="p-3.5">
      <div className="text-lg font-black tracking-tight text-slate-900 leading-tight">
        Você foi convidado(a)!
      </div>
      <div className="text-xs text-slate-700 mt-1.5">
        <strong>{leaderName || 'Nome da liderança'}</strong> está te convidando para participar da rede{tenantName ? ` de ${tenantName}` : ''}.
      </div>
      <div className="text-xs text-slate-500 mt-2 whitespace-pre-line leading-relaxed">
        {subtitle || 'Preencha seus dados para continuar e entrar no grupo/canal do WhatsApp.'}
      </div>
    </div>
  </div>
);

// ---------- Cascade badge ----------
// Shows the admin exactly WHERE the fallback value is coming from when they
// leave a field blank. Reduces confusion between link/leader/dobrada/tenant.
const CascadeHint = ({ activeSource }) => (
  <div className="flex items-start gap-2 rounded-md bg-sky-50 border border-sky-100 px-3 py-2 text-[11px] text-sky-900 leading-snug">
    <Info className="h-3.5 w-3.5 text-sky-600 flex-shrink-0 mt-0.5" />
    <div>
      <strong className="font-semibold">Vazio herda de:</strong>{' '}
      {activeSource || 'liderança → dobrada → cliente → MeAdd'}
    </div>
  </div>
);

// ---------- Main sheet ----------
/**
 * Personalization sheet used across `/links`, `DobradaDetailDrawer` and
 * `LeaderDetailsDrawer`. Plain-language tabs + always-visible previews.
 *
 * Props:
 *  - link:      short_link doc (with `id`, `token`, `og_*`, `capture_*`, `leader_invite_*`)
 *  - leader:    optional leader doc (`apelido`/`nome`) used to render dynamic defaults
 *  - open, onOpenChange
 *  - onSaved(): parent refresh hook
 *  - canEdit
 *  - showInviteTab: default true when link.leader_invite_enabled (toggleable)
 */
export const LinkMetaSheet = ({
  link,
  leader = null,
  open,
  onOpenChange,
  onSaved,
  canEdit = true,
}) => {
  const { activeTenant } = useAuth();
  const apiBase = (BACKEND_URL || '').replace(/\/$/, '');
  const host = apiBase.replace(/^https?:\/\//, '').replace(/\/.*$/, '');

  const [metaForm, setMetaForm] = useState({
    og_title: '',
    og_description: '',
    og_image_url: '',
    capture_hero_image_url: '',
    capture_subtitle: '',
    leader_invite_enabled: false,
    leader_invite_hero_image_url: '',
    leader_invite_title: '',
    leader_invite_subtitle: '',
  });
  const [metaSaving, setMetaSaving] = useState(false);
  const [metaTab, setMetaTab] = useState('share');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!link) return;
    setMetaForm({
      og_title: link.og_title || '',
      og_description: link.og_description || '',
      og_image_url: link.og_image_url || '',
      capture_hero_image_url: link.capture_hero_image_url || '',
      capture_subtitle: link.capture_subtitle || '',
      leader_invite_enabled: !!link.leader_invite_enabled,
      leader_invite_hero_image_url: link.leader_invite_hero_image_url || '',
      leader_invite_title: link.leader_invite_title || '',
      leader_invite_subtitle: link.leader_invite_subtitle || '',
    });
    setMetaTab('share');
  }, [link]);

  const leaderFirstName = useMemo(
    () => leader?.apelido || (leader?.nome || leader?.nome_completo || '').split(' ')[0] || '',
    [leader]
  );

  // Dynamic default titles used when the field is blank
  const defaultShareTitle = leaderFirstName
    ? `${leaderFirstName} te convidou para a ${activeTenant?.name || 'nossa rede'}`
    : `Você foi convidado(a) para a ${activeTenant?.name || 'nossa rede'}`;
  const defaultShareDescription = `Toque para entrar na rede da ${activeTenant?.name || 'nossa rede'} no WhatsApp.`;

  const defaultInviteTitle = leaderFirstName
    ? `${leaderFirstName} convidou você para liderar!`
    : `Seja uma liderança em ${activeTenant?.name || 'nossa rede'}`;
  const defaultInviteDescription = 'Cadastre-se como liderança e ajude a mobilizar apoiadores na nossa rede.';

  const saveMeta = async () => {
    if (!link) return;
    setMetaSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/links/${link.id}`,
        {
          og_title: metaForm.og_title || null,
          og_description: metaForm.og_description || null,
          og_image_url: metaForm.og_image_url || null,
          capture_hero_image_url: metaForm.capture_hero_image_url || null,
          capture_subtitle: metaForm.capture_subtitle || null,
          leader_invite_enabled: metaForm.leader_invite_enabled,
          leader_invite_hero_image_url: metaForm.leader_invite_hero_image_url || null,
          leader_invite_title: metaForm.leader_invite_title || null,
          leader_invite_subtitle: metaForm.leader_invite_subtitle || null,
        },
        { withCredentials: true }
      );
      toast.success('Personalização salva!');
      onOpenChange(false);
      onSaved?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Falha ao salvar.');
    } finally {
      setMetaSaving(false);
    }
  };

  const clearMeta = async () => {
    if (!link) return;
    setMetaSaving(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/links/${link.id}`,
        {
          og_title: null,
          og_description: null,
          og_image_url: null,
          capture_hero_image_url: null,
          capture_subtitle: null,
          // Keep leader_invite_enabled as-is (destructive to toggle it here)
          leader_invite_hero_image_url: null,
          leader_invite_title: null,
          leader_invite_subtitle: null,
        },
        { withCredentials: true }
      );
      toast.success('Personalização resetada — voltamos aos padrões.');
      onOpenChange(false);
      onSaved?.();
    } catch (_) {
      toast.error('Falha ao resetar.');
    } finally {
      setMetaSaving(false);
    }
  };

  const inviteShareUrl = link?.leader_invite_token && activeTenant?.slug
    ? `${apiBase}/api/og/cl/${activeTenant.slug}/${link.leader_invite_token}`
    : '';

  const copyInviteUrl = async () => {
    if (!inviteShareUrl) return;
    try {
      await navigator.clipboard.writeText(inviteShareUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error('Não consegui copiar. Tente selecionar manualmente.');
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="link-meta-sheet"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 font-outfit">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            Personalizar este link
          </SheetTitle>
          <SheetDescription className="text-xs">
            Cada aba controla um momento diferente da jornada. Deixe em branco
            para herdar da liderança, dobrada ou cliente.
            {link && (
              <span className="block mt-1.5 font-mono text-[11px] text-emerald-700">/{link.token}</span>
            )}
          </SheetDescription>
        </SheetHeader>

        <Tabs value={metaTab} onValueChange={setMetaTab} className="py-4">
          <TabsList className="grid grid-cols-3 w-full h-auto">
            <TabsTrigger
              value="share"
              data-testid="meta-tab-share"
              className="flex flex-col items-center gap-1 py-2 text-[11px] whitespace-normal"
            >
              <Smartphone className="h-4 w-4" />
              <span className="leading-tight">Como aparece no WhatsApp</span>
            </TabsTrigger>
            <TabsTrigger
              value="capture"
              data-testid="meta-tab-capture"
              className="flex flex-col items-center gap-1 py-2 text-[11px] whitespace-normal"
            >
              <Target className="h-4 w-4" />
              <span className="leading-tight">Página que abre depois</span>
            </TabsTrigger>
            <TabsTrigger
              value="invite"
              data-testid="meta-tab-invite"
              className="flex flex-col items-center gap-1 py-2 text-[11px] whitespace-normal"
            >
              <UserPlus className="h-4 w-4" />
              <span className="leading-tight">Convidar outras lideranças</span>
            </TabsTrigger>
          </TabsList>

          {/* ============================================================ */}
          {/* TAB 1: Como aparece no WhatsApp                              */}
          {/* ============================================================ */}
          <TabsContent value="share" className="space-y-4 mt-5">
            <p className="text-xs text-slate-600 leading-snug">
              Este é o card que aparece quando alguém compartilha o link no WhatsApp
              (ou Instagram, Facebook). Serve para chamar a atenção antes do clique.
            </p>

            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Prévia ao vivo
              </div>
              <WhatsAppPreviewCard
                image={metaForm.og_image_url || activeTenant?.logo_url || ''}
                title={metaForm.og_title || defaultShareTitle}
                description={metaForm.og_description || defaultShareDescription}
                host={host}
              />
              <p className="text-[10px] text-slate-500">
                Atualiza conforme você digita abaixo.
              </p>
            </div>

            <CascadeHint activeSource="liderança → dobrada → cliente → MeAdd" />

            <ImageUpload
              value={metaForm.og_image_url}
              onChange={(url) => setMetaForm({ ...metaForm, og_image_url: url })}
              folder={`meadd/tenants/${activeTenant?.id}/links`}
              label="Imagem de destaque (1200×628)"
              aspect="aspect-[1200/628]"
              maxWidth="max-w-full"
              testId="link-meta-image"
            />

            <div>
              <Label htmlFor="og_title" className="text-sm font-semibold text-slate-700">
                Título <span className="text-slate-400 font-normal">(até 90 caracteres)</span>
              </Label>
              <Input
                id="og_title"
                value={metaForm.og_title}
                onChange={(e) => setMetaForm({ ...metaForm, og_title: e.target.value.slice(0, 90) })}
                placeholder={defaultShareTitle}
                className="mt-1.5"
                data-testid="link-meta-title"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                {metaForm.og_title.length}/90 caracteres. Em branco: <em>&ldquo;{defaultShareTitle}&rdquo;</em>.
              </p>
            </div>

            <div>
              <Label htmlFor="og_description" className="text-sm font-semibold text-slate-700">
                Descrição <span className="text-slate-400 font-normal">(até 200 caracteres)</span>
              </Label>
              <Textarea
                id="og_description"
                value={metaForm.og_description}
                onChange={(e) => setMetaForm({ ...metaForm, og_description: e.target.value.slice(0, 200) })}
                placeholder={defaultShareDescription}
                rows={3}
                className="mt-1.5 resize-none"
                data-testid="link-meta-description"
              />
              <p className="text-[11px] text-slate-500 mt-1">{metaForm.og_description.length}/200 caracteres.</p>
            </div>
          </TabsContent>

          {/* ============================================================ */}
          {/* TAB 2: Página que abre depois                                */}
          {/* ============================================================ */}
          <TabsContent value="capture" className="space-y-4 mt-5">
            <p className="text-xs text-slate-600 leading-snug">
              Depois que o apoiador toca no link, ele cai numa página com um formulário.
              Você pode personalizar o banner e a mensagem que aparecem ali.
            </p>

            <div className="space-y-1.5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                Prévia ao vivo
              </div>
              <CapturePreviewCard
                image={metaForm.capture_hero_image_url}
                subtitle={metaForm.capture_subtitle}
                leaderName={leaderFirstName || leader?.nome || 'Liderança'}
                tenantName={activeTenant?.name}
                tenantColor={activeTenant?.primary_color}
              />
            </div>

            <CascadeHint activeSource="liderança → dobrada → cliente → MeAdd" />

            <ImageUpload
              value={metaForm.capture_hero_image_url}
              onChange={(url) => setMetaForm({ ...metaForm, capture_hero_image_url: url })}
              folder={`meadd/tenants/${activeTenant?.id}/capture`}
              label="Banner do formulário (1200×628)"
              aspect="aspect-[1200/628]"
              maxWidth="max-w-full"
              testId="link-capture-hero"
            />

            <div>
              <Label htmlFor="capture_subtitle" className="text-sm font-semibold text-slate-700">
                Mensagem antes dos campos <span className="text-slate-400 font-normal">(até 240 caracteres)</span>
              </Label>
              <Textarea
                id="capture_subtitle"
                value={metaForm.capture_subtitle}
                onChange={(e) => setMetaForm({ ...metaForm, capture_subtitle: e.target.value.slice(0, 240) })}
                placeholder="Ex.: Estamos construindo uma cidade melhor — entre na nossa rede e receba novidades exclusivas em primeira mão."
                rows={4}
                className="mt-1.5 resize-none"
                data-testid="link-capture-subtitle"
              />
              <p className="text-[11px] text-slate-500 mt-1">{metaForm.capture_subtitle.length}/240 caracteres.</p>
            </div>
          </TabsContent>

          {/* ============================================================ */}
          {/* TAB 3: Convidar outras lideranças                            */}
          {/* ============================================================ */}
          <TabsContent value="invite" className="space-y-4 mt-5">
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 text-xs text-emerald-900 leading-relaxed">
              Ao habilitar, este link ganha uma <strong>segunda URL pública</strong> que serve
              para cadastrar novas lideranças (não apoiadores). Quem entrar por lá vira
              liderança e ganha seu próprio link automático — que herda o mesmo destino
              deste link original.
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!metaForm.leader_invite_enabled}
                onChange={(e) =>
                  setMetaForm({ ...metaForm, leader_invite_enabled: e.target.checked })
                }
                className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                data-testid="link-meta-invite-enabled"
              />
              <span className="text-sm font-medium text-slate-900">
                Habilitar captação de lideranças por este link
              </span>
            </label>

            {metaForm.leader_invite_enabled && (
              <>
                {inviteShareUrl && (
                  <div className="flex items-center gap-2 rounded-md bg-white border border-emerald-200 p-2">
                    <code className="flex-1 truncate text-[11px] font-mono text-emerald-800 px-1" data-testid="link-meta-invite-url">
                      {inviteShareUrl}
                    </code>
                    <Button type="button" size="icon" variant="outline" className="h-7 w-7" onClick={copyInviteUrl}>
                      {copied ? <Check className="h-3 w-3 text-emerald-600" /> : <Copy className="h-3 w-3" />}
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="h-7 w-7"
                      onClick={() => window.open(inviteShareUrl, '_blank')}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                  </div>
                )}

                <div className="space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                    Prévia ao vivo
                  </div>
                  <WhatsAppPreviewCard
                    image={metaForm.leader_invite_hero_image_url || metaForm.og_image_url || activeTenant?.logo_url || ''}
                    title={metaForm.leader_invite_title || defaultInviteTitle}
                    description={metaForm.leader_invite_subtitle || defaultInviteDescription}
                    host={host}
                  />
                </div>

                <CascadeHint activeSource="dobrada → cliente → MeAdd" />

                <ImageUpload
                  value={metaForm.leader_invite_hero_image_url}
                  onChange={(url) =>
                    setMetaForm({ ...metaForm, leader_invite_hero_image_url: url })
                  }
                  folder={`meadd/tenants/${activeTenant?.id}/leader-invite`}
                  label="Banner do convite (1200×628)"
                  aspect="aspect-[1200/628]"
                  maxWidth="max-w-full"
                  testId="link-meta-invite-image"
                />

                <div>
                  <Label className="text-sm font-semibold text-slate-700">Título do convite</Label>
                  <Input
                    value={metaForm.leader_invite_title}
                    onChange={(e) =>
                      setMetaForm({ ...metaForm, leader_invite_title: e.target.value.slice(0, 80) })
                    }
                    placeholder={defaultInviteTitle}
                    maxLength={80}
                    className="mt-1.5"
                    data-testid="link-meta-invite-title"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {metaForm.leader_invite_title.length}/80 caracteres. Em branco: <em>&ldquo;{defaultInviteTitle}&rdquo;</em>.
                  </p>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-700">Mensagem para o convidado</Label>
                  <Textarea
                    value={metaForm.leader_invite_subtitle}
                    onChange={(e) =>
                      setMetaForm({ ...metaForm, leader_invite_subtitle: e.target.value.slice(0, 240) })
                    }
                    placeholder="Ex.: Vamos multiplicar essa rede juntos! Preencha seus dados e você recebe seu próprio link personalizado."
                    rows={3}
                    maxLength={240}
                    className="mt-1.5 resize-none"
                    data-testid="link-meta-invite-subtitle"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    {metaForm.leader_invite_subtitle.length}/240 caracteres.
                  </p>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>

        <SheetFooter className="flex-row gap-2 sm:justify-between border-t border-slate-100 pt-4">
          <Button
            type="button"
            variant="ghost"
            onClick={clearMeta}
            disabled={metaSaving || !link}
            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
            data-testid="link-meta-clear-btn"
          >
            <Trash2 className="h-4 w-4 mr-1.5" />
            Resetar tudo
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={metaSaving}>
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={saveMeta}
              disabled={metaSaving || !canEdit}
              className="bg-emerald-600 hover:bg-emerald-700 gap-1.5"
              data-testid="link-meta-save-btn"
            >
              {metaSaving ? 'Salvando…' : <><Check className="h-4 w-4" />Salvar</>}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
